/**
 * MoradoresSelfRegSidebar — inline right panel shown alongside the moradores list.
 *
 * Shows pending self-registrations and a "copy signup link" button.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  UserPlus, Loader2, CheckCircle, XCircle, RefreshCw,
  Copy, Phone, Mail, Building2, FileText, Send, ChevronDown, ChevronUp, Camera,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import toast from 'react-hot-toast';
import {
  listSelfRegistrations,
  approveSelfRegistration,
  rejectSelfRegistration,
  getSignupLink,
  resendWelcomeEmail,
  SelfRegistration,
} from '../../../services/selfRegistrationApi';
import { makeAbsolutePhotoUrl } from '../../../services/api';
import { useKeycloak } from '../../../contexts/KeycloakContext';
import { listIdentityPhotoRequests } from '../../../services/identityPhotoRequestApi';
import IdentityPhotoRequestsPanel from './IdentityPhotoRequestsPanel';

interface MoradoresSelfRegSidebarProps {
  customerId: string;
  lightTheme?: boolean;
}

export default function MoradoresSelfRegSidebar({ customerId, lightTheme = false }: MoradoresSelfRegSidebarProps) {
  const { isSuperAdmin } = useKeycloak();
  const [items, setItems] = useState<SelfRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [copyingLink, setCopyingLink] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Identity-photo requests live in their own slide-over: deciding a troca means comparing two
  // faces side by side, which does not fit this column. The count is loaded here so the queue is
  // visible at all — one nobody notices is one nobody works.
  const [showPhotoRequests, setShowPhotoRequests] = useState(false);
  const [photoRequestCount, setPhotoRequestCount] = useState(0);

  const [showApproved, setShowApproved] = useState(false);
  const [approvedItems, setApprovedItems] = useState<SelfRegistration[]>([]);
  const [loadingApproved, setLoadingApproved] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const resp = await listSelfRegistrations(customerId, 'pending');
      setItems(resp.items || []);
      setLoadError(null);
      // Independently failable: a photo-request count that cannot be read must not make the
      // moradores queue look broken.
      try {
        const photos = await listIdentityPhotoRequests(customerId, 'pending');
        setPhotoRequestCount((photos.items || []).length);
      } catch {
        setPhotoRequestCount(0);
      }
    } catch (err: any) {
      // Shown inline rather than as a toast: this also runs on a 60s interval, so a persistent
      // failure would raise a toast every minute. Swallowing it entirely was worse — a 403 or a
      // dropped connection rendered as "Nenhum cadastro pendente", which reads as "all clear".
      setLoadError(err?.message || 'Erro ao carregar os auto-cadastros');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  const handleApprove = async (reg: SelfRegistration) => {
    setProcessingId(reg.id);
    try {
      const resp = await approveSelfRegistration(customerId, reg.id);
      if (resp.success) {
        if (resp.email_status === 'failed') {
          toast.error(resp.message || 'Morador aprovado, mas o e-mail de senha falhou ao enviar.');
        } else {
          toast.success(resp.message || 'Morador aprovado!');
        }
        setItems(prev => prev.filter(r => r.id !== reg.id));
      } else {
        toast.error(resp.message || 'Erro ao aprovar');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao aprovar');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (reg: SelfRegistration) => {
    setProcessingId(reg.id);
    try {
      await rejectSelfRegistration(customerId, reg.id, rejectReason || undefined);
      toast.success('Cadastro rejeitado.');
      setItems(prev => prev.filter(r => r.id !== reg.id));
      setRejectingId(null);
      setRejectReason('');
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao rejeitar');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCopyLink = async () => {
    setCopyingLink(true);
    try {
      const resp = await getSignupLink(customerId);
      await navigator.clipboard.writeText(resp.signup_url);
      toast.success('Link copiado!');
    } catch (err: any) {
      // Report why. The generic "Erro ao obter link" hid real causes — a 403 from the
      // permission gate and a clipboard permission denial looked identical.
      toast.error(err?.message || 'Erro ao obter link');
    } finally {
      setCopyingLink(false);
    }
  };

  const loadApproved = useCallback(async () => {
    setLoadingApproved(true);
    try {
      const resp = await listSelfRegistrations(customerId, 'approved');
      setApprovedItems((resp.items || []).slice(0, 10));
    } catch (err: any) {
      // Unlike load() this only runs when the user expands the section, so a toast can't spam.
      toast.error(err?.message || 'Erro ao carregar os cadastros aprovados');
    } finally {
      setLoadingApproved(false);
    }
  }, [customerId]);

  const toggleApproved = () => {
    if (!showApproved) loadApproved();
    setShowApproved(v => !v);
  };

  const handleResendWelcome = async (reg: SelfRegistration) => {
    setResendingId(reg.id);
    try {
      await resendWelcomeEmail(customerId, reg.id);
      toast.success(`Boas-vindas reenviado para ${reg.email}`);
    } catch (err: any) {
      toast.error(err?.message || 'Erro ao reenviar e-mail');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <>
      <IdentityPhotoRequestsPanel
        open={showPhotoRequests}
        customerId={customerId}
        onClose={() => { setShowPhotoRequests(false); load(); }}
      />

      {/* Photo preview overlay */}
      {previewPhoto && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80"
          onClick={() => setPreviewPhoto(null)}
        >
          <img
            src={previewPhoto}
            alt="Foto"
            className="max-w-sm max-h-[80vh] rounded-lg object-contain"
          />
        </div>
      )}

      <div className={cn(
        "w-64 flex-shrink-0 flex flex-col gap-3 border-l pl-3 pt-0.5",
        lightTheme ? "border-gray-200" : "border-gray-800/50"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <p className={cn("text-xs font-semibold uppercase tracking-wider", lightTheme ? "text-gray-500" : "text-gray-400")}>Novos Moradores</p>
            {items.length > 0 && (
              <span className="bg-emerald-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {items.length}
              </span>
            )}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className={cn(
              "p-1 rounded transition-colors",
              lightTheme ? "text-gray-400 hover:text-gray-700" : "text-gray-500 hover:text-white"
            )}
            title="Recarregar"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
          </button>
        </div>

        {/* Identity-photo requests */}
        <button
          onClick={() => setShowPhotoRequests(true)}
          className={cn(
            "flex items-center justify-center gap-2 py-1.5 px-2 rounded-lg text-xs font-semibold transition-colors border",
            lightTheme
              ? "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
              : "bg-gray-800/60 hover:bg-gray-800 text-gray-300 border-gray-700/40"
          )}
        >
          <Camera className="h-3 w-3" />
          Fotos de identificação
          {photoRequestCount > 0 && (
            <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 rounded-full">
              {photoRequestCount}
            </span>
          )}
        </button>

        {/* Copy signup link */}
        <button
          onClick={handleCopyLink}
          disabled={copyingLink}
          className={cn(
            "flex items-center justify-center gap-2 py-1.5 px-2 rounded-lg text-xs font-semibold transition-colors border",
            lightTheme
              ? "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
              : "bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-400 border-emerald-700/40"
          )}
        >
          {copyingLink ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          Copiar link de cadastro
        </button>

        {/* List */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-0 custom-scrollbar">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className={cn("h-5 w-5 animate-spin", lightTheme ? "text-gray-400" : "text-gray-600")} />
            </div>
          ) : loadError && items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
              <XCircle className="h-7 w-7 text-red-400" />
              <p className={cn("text-xs px-2", lightTheme ? "text-red-600" : "text-red-400")}>{loadError}</p>
              <button
                onClick={load}
                className={cn(
                  "text-xs font-semibold underline",
                  lightTheme ? "text-gray-600 hover:text-gray-900" : "text-gray-400 hover:text-white"
                )}
              >
                Tentar novamente
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
              <CheckCircle className={cn("h-7 w-7", lightTheme ? "text-gray-300" : "text-gray-700")} />
              <p className={cn("text-xs", lightTheme ? "text-gray-400" : "text-gray-500")}>Nenhum cadastro pendente</p>
            </div>
          ) : (
            items.map(reg => {
              const isProcessing = processingId === reg.id;
              const isRejecting = rejectingId === reg.id;
              const name = `${reg.first_name} ${reg.last_name}`.trim();
              const date = new Date(reg.created_at).toLocaleDateString('pt-BR');
              const unitLabel = reg.unit
                ? `${reg.unit.block.name} / Ap ${reg.unit.number}`
                : null;
              const faceUrl = reg.face_photo_url ? makeAbsolutePhotoUrl(reg.face_photo_url) : null;
              const docUrl = reg.doc_photo_url ? makeAbsolutePhotoUrl(reg.doc_photo_url) : null;

              return (
                <div
                  key={reg.id}
                  className={cn(
                    "rounded-lg p-2.5 space-y-2 border",
                    lightTheme ? "bg-white border-gray-200 shadow-sm" : "bg-gray-800/50 border-gray-700/50"
                  )}
                >
                  {/* Person info */}
                  <div className="flex items-start gap-2">
                    <div
                      className={cn(
                        'w-7 h-9 rounded-md flex-shrink-0 flex items-center justify-center border overflow-hidden',
                        faceUrl
                          ? 'border-emerald-700/50 cursor-pointer'
                          : lightTheme ? 'border-gray-200 bg-gray-100' : 'border-gray-700 bg-gray-800'
                      )}
                      onClick={() => faceUrl && setPreviewPhoto(faceUrl)}
                    >
                      {faceUrl ? (
                        <img src={faceUrl} alt={name} className="w-full h-full object-cover" />
                      ) : (
                        <UserPlus className={cn("h-4 w-4", lightTheme ? "text-gray-400" : "text-gray-500")} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className={cn("text-xs font-medium truncate", lightTheme ? "text-gray-900" : "text-white")}>{name}</p>
                      <div className={cn("flex items-center gap-1 text-xs mt-0.5 truncate", lightTheme ? "text-gray-500" : "text-gray-500")}>
                        <Mail className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{reg.email}</span>
                      </div>
                      {reg.phone && (
                        <div className={cn("flex items-center gap-1 text-xs mt-0.5", lightTheme ? "text-gray-400" : "text-gray-600")}>
                          <Phone className="h-3 w-3 flex-shrink-0" />
                          <span>{reg.phone}</span>
                        </div>
                      )}
                      {unitLabel && (
                        <div className={cn("flex items-center gap-1 text-xs mt-0.5", lightTheme ? "text-emerald-700" : "text-emerald-600")}>
                          <Building2 className="h-3 w-3 flex-shrink-0" />
                          <span className="truncate">{unitLabel}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-0.5">
                        <p className={cn("text-xs", lightTheme ? "text-gray-400" : "text-gray-600")}>{date}</p>
                        {docUrl && (
                          <button
                            onClick={() => setPreviewPhoto(docUrl)}
                            className={cn(
                              "transition-colors",
                              lightTheme ? "text-gray-400 hover:text-gray-700" : "text-gray-600 hover:text-gray-300"
                            )}
                            title="Ver documento"
                          >
                            <FileText className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Reject reason input */}
                  {isRejecting && (
                    <input
                      type="text"
                      value={rejectReason}
                      onChange={e => setRejectReason(e.target.value)}
                      placeholder="Motivo (opcional)"
                      className={cn(
                        "w-full rounded px-2 py-1 text-xs focus:outline-none border",
                        lightTheme
                          ? "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-red-500"
                          : "bg-gray-900 border-gray-700 text-white placeholder-gray-500 focus:border-red-600"
                      )}
                    />
                  )}

                  {/* Action buttons */}
                  {!isRejecting ? (
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => handleApprove(reg)}
                        disabled={isProcessing}
                        className={cn(
                          "flex-1 flex items-center justify-center gap-1 py-1 rounded text-xs font-semibold border transition-colors",
                          lightTheme
                            ? "bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                            : "bg-green-600/20 hover:bg-green-600/30 text-green-400 border-green-700/50"
                        )}
                      >
                        {isProcessing ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <CheckCircle className="h-3 w-3" />
                        )}
                        Aprovar
                      </button>
                      <button
                        onClick={() => { setRejectingId(reg.id); setRejectReason(''); }}
                        disabled={isProcessing}
                        className={cn(
                          "flex-1 flex items-center justify-center gap-1 py-1 rounded text-xs font-semibold border transition-colors",
                          lightTheme
                            ? "bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                            : "bg-red-600/10 hover:bg-red-600/20 text-red-400 border-red-700/30"
                        )}
                      >
                        <XCircle className="h-3 w-3" />
                        Rejeitar
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => handleReject(reg)}
                        disabled={isProcessing}
                        className={cn(
                          "flex-1 flex items-center justify-center gap-1 py-1 rounded text-xs font-semibold border transition-colors",
                          lightTheme
                            ? "bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
                            : "bg-red-600/20 hover:bg-red-600/30 text-red-400 border-red-700/50"
                        )}
                      >
                        {isProcessing ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <XCircle className="h-3 w-3" />
                        )}
                        Confirmar
                      </button>
                      <button
                        onClick={() => { setRejectingId(null); setRejectReason(''); }}
                        className={cn(
                          "flex-1 flex items-center justify-center gap-1 py-1 rounded text-xs font-semibold border transition-colors",
                          lightTheme
                            ? "bg-gray-100 hover:bg-gray-200 text-gray-600 border-gray-300"
                            : "bg-gray-800 hover:bg-gray-700 text-gray-400 border-gray-700"
                        )}
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Super-admin: Reenviar boas-vindas */}
        {isSuperAdmin && (
          <div className={cn("border-t mt-1", lightTheme ? "border-gray-200" : "border-gray-700/50")}>
            <button
              onClick={toggleApproved}
              className={cn(
                "w-full flex items-center justify-between py-2 text-xs transition-colors",
                lightTheme ? "text-gray-500 hover:text-gray-700" : "text-gray-500 hover:text-gray-300"
              )}
            >
              <div className="flex items-center gap-1.5">
                <Send className={cn("h-3 w-3", lightTheme ? "text-blue-500" : "text-blue-400")} />
                <span>Reenviar boas-vindas</span>
              </div>
              {showApproved ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>

            {showApproved && (
              <div className="space-y-1 max-h-40 overflow-y-auto pb-1">
                {loadingApproved ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className={cn("h-4 w-4 animate-spin", lightTheme ? "text-gray-400" : "text-gray-600")} />
                  </div>
                ) : approvedItems.length === 0 ? (
                  <p className={cn("text-[10px] text-center py-2", lightTheme ? "text-gray-400" : "text-gray-600")}>Nenhum aprovado recente</p>
                ) : (
                  approvedItems.map(reg => {
                    const name = `${reg.first_name} ${reg.last_name}`.trim();
                    const isResending = resendingId === reg.id;
                    return (
                      <div key={reg.id} className={cn("flex items-center gap-1.5 rounded px-2 py-1.5", lightTheme ? "bg-gray-50" : "bg-gray-800/30")}>
                        <div className="flex-1 min-w-0">
                          <p className={cn("text-[10px] font-medium truncate", lightTheme ? "text-gray-900" : "text-white")}>{name}</p>
                          <p className={cn("text-[9px] truncate", lightTheme ? "text-gray-400" : "text-gray-600")}>{reg.email}</p>
                        </div>
                        <button
                          onClick={() => handleResendWelcome(reg)}
                          disabled={isResending}
                          className={cn(
                            "flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold border disabled:opacity-50 transition-colors flex-shrink-0",
                            lightTheme
                              ? "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                              : "bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 border-blue-700/40"
                          )}
                        >
                          {isResending ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Send className="h-2.5 w-2.5" />}
                          Reenviar
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
